import React, { useState } from 'react';
import { CheckCircledIcon, BarChartIcon } from '@radix-ui/react-icons';
import { useParams, useNavigate } from 'react-router-dom';
import {
	Card,
	CardContent,
	Button,
	Badge,
	Spinner,
	Alert,
	AlertTitle,
	EmptyState,
	useToast,
	StatusBadge, PostLink } from '../ui';
import { StatCard, ScanProgress, RiskVerdict } from '../composite';
import { useScanData } from '../features/scans';
import { PATHS } from '../../constants/Paths';
import ScanFailureActions from '../features/scans/ScanFailureActions';

/**
 * ScanDetail - Modern scan detail page component
 * 
 * Features:
 * - Comprehensive scan information display
 * - Tabbed interface for overview, flagged content, analysis
 * - Real-time progress updates
 * - Proper UI component structure
 * - Print-friendly styling
 */
export const ScanDetailPage = () => {
	const toast = useToast();
	const { id } = useParams();
	const navigate = useNavigate();
	const [activeTab, setActiveTab] = useState('overview');

	const {
		scan,
		flaggedPosts,
		loading,
		error,
		refresh,
		isScanning,
		isCompleted,
	} = useScanData(id);

	const formatDate = (date) => {
		if (!date) return 'Unknown';
		return new Date(date).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	const formatDuration = (seconds) => {
		if (!seconds) return 'N/A';
		const mins = Math.floor(seconds / 60);
		const secs = seconds % 60;
		return `${mins}m ${secs}s`;
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center">
					<Spinner size="lg" className="mx-auto mb-4" />
					<p className="text-muted-foreground">Loading scan details...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="max-w-4xl mx-auto p-6">
				<Alert variant="destructive">
					<AlertTitle>Error Loading Scan</AlertTitle>
					<p className="mt-2">{error}</p>
					<Button
						onClick={() => navigate(PATHS.SCAN_LIST)}
						className="mt-4"
						variant="outline"
					>
						Return to Scans
					</Button>
				</Alert>
			</div>
		);
	}

	if (!scan) {
		return (
			<div className="max-w-4xl mx-auto p-6">
				<Alert>
					<AlertTitle>Scan Not Found</AlertTitle>
					<p className="mt-2">The requested scan could not be found.</p>
					<Button
						onClick={() => navigate(PATHS.SCAN_LIST)}
						className="mt-4"
						variant="outline"
					>
						Return to Scans
					</Button>
				</Alert>
			</div>
		);
	}


	return (
		<>
			{/* Print Styles */}
			<style>
				{`
					@media print {
						/* Hide navigation and non-essential elements */
						.print-hide, 
						nav, 
						.sidebar, 
						.print\\:hidden,
						button:not(.print-show),
						.tab-navigation {
							display: none !important;
						}
						
						/* Ensure print content takes full width */
						.print-content {
							width: 100% !important;
							max-width: 100% !important;
							margin: 0 !important;
							padding: 20px !important;
						}
						
						/* Optimize spacing for print */
						.print-section {
							margin-bottom: 20px !important;
							page-break-inside: avoid;
						}
						
						/* Ensure text is black for printing */
						* {
							color: black !important;
							-webkit-print-color-adjust: exact;
						}
						
						/* Page break controls */
						.page-break-before {
							page-break-before: always;
						}
						
						.page-break-avoid {
							page-break-inside: avoid;
						}
					}
				`}
			</style>

			<div className="min-h-screen bg-muted">
				<div className="max-w-7xl mx-auto p-6 print-content">
					{/* Header */}
					<div className="mb-6 print-section">
						<div className="flex items-center justify-between mb-4">
							<div>
								<Button
									variant="ghost"
									onClick={() => navigate(PATHS.SCAN_LIST)}
									className="mb-2 print-hide"
								>
									← Back to Scans
								</Button>
								<h1 className="text-3xl font-bold text-foreground">Scan Report</h1>
								<p className="text-muted-foreground mt-1">
									{scan.client?.name} - @{scan.account?.handle} ({scan.platform})
								</p>
							</div>
							<div className="text-right">
								<StatusBadge status={scan.status} />
							</div>
						</div>

						{/* Scan Metadata */}
						<div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm print-section">
							<Card>
								<CardContent className="p-4">
									<div className="text-muted-foreground">Started</div>
									<div className="font-medium">{formatDate(scan.startedAt)}</div>
								</CardContent>
							</Card>
							{scan.completedAt && (
								<Card>
									<CardContent className="p-4">
										<div className="text-muted-foreground">Completed</div>
										<div className="font-medium">{formatDate(scan.completedAt)}</div>
									</CardContent>
								</Card>
							)}
							{scan.scanType && scan.scanType !== 'Standard' && (
								<Card>
									<CardContent className="p-4">
										<div className="text-muted-foreground">Type</div>
										<div className="font-medium">{scan.scanType}</div>
									</CardContent>
								</Card>
							)}
							{scan.duration && (
								<Card>
									<CardContent className="p-4">
										<div className="text-muted-foreground">Duration</div>
										<div className="font-medium">{formatDuration(scan.duration)}</div>
									</CardContent>
								</Card>
							)}
						</div>

						{/* Smart Retry Component for Failed Scans */}
						<ScanFailureActions
							scan={scan}
							onRetryStarted={() => {
								// Already on the scan's own page - the progress card takes
								// over in place as soon as the status flips. (This used to
								// navigate via PATHS.SCAN_PROGRESS, a duplicate constant that
								// no longer exists, so retry threw a TypeError instead.)
								toast.info('Retry started', { description: 'Progress updates here as it runs.' });
								refresh();
							}}
							onRetryComplete={() => {
								toast.success('Retry complete');
								refresh();
							}}
							className="mt-6"
						/>
					</div>

					{/* Tab Navigation */}
					<div className="border-b border-border mb-6 print-hide tab-navigation">
						<nav className="flex space-x-8">
							<button
								onClick={() => setActiveTab('overview')}
								className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'overview'
									? 'border-brand-500 text-brand-600'
									: 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
									}`}
							>
								Overview
							</button>
							<button
								onClick={() => setActiveTab('flagged')}
								className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'flagged'
									? 'border-brand-500 text-brand-600'
									: 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
									}`}
							>
								Flagged Content
								{flaggedPosts.length > 0 && (
									<Badge variant="secondary" className="ml-2">
										{flaggedPosts.length}
									</Badge>
								)}
							</button>
							<button
								onClick={() => setActiveTab('analysis')}
								className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'analysis'
									? 'border-brand-500 text-brand-600'
									: 'border-transparent text-muted-foreground hover:text-foreground hover:border-input'
									}`}
							>
								Analysis
							</button>
						</nav>
					</div>

					{/* Tab Content */}
					<div className="space-y-6">
						{/* Overview Tab */}
						{activeTab === 'overview' && (
							<div className="space-y-6">
								{/* Enhanced Progress Section with detailed window/post information */}
								{isScanning && (
									<ScanProgress
										scanId={id}
										onComplete={() => refresh()}
										onError={(error) => console.error('Scan error:', error)}
									/>
								)}

								{/* Risk verdict (two-number model: status + severity + exposure + action) */}
								{isCompleted && scan.results && (
									<RiskVerdict results={scan.results} />
								)}

								{/* Since last scan - the question a manager actually asks is not
								    "what is the risk" but "did it get worse". Both scans are
								    already stored; this costs nothing new. */}
								{isCompleted && scan.results && scan.previous?.results && (() => {
									const prev = scan.previous.results;
									const cur = scan.results;
									const deltas = [
										{ label: 'Flagged posts', now: cur.flaggedPosts || 0, before: prev.flaggedPosts || 0, badIsUp: true },
										{ label: 'Risk score', now: cur.riskScore || 0, before: prev.riskScore || 0, badIsUp: true },
										{ label: 'Posts scanned', now: cur.totalPosts || 0, before: prev.totalPosts || 0, badIsUp: false },
									];
									const prevDate = scan.previous.completedAt
										? new Date(scan.previous.completedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
										: 'previous scan';
									return (
										<div className="rounded-xl border border-border bg-card shadow-card p-4">
											<div className="flex items-center justify-between">
												<span className="text-sm font-semibold text-foreground">Since last scan</span>
												<span className="text-xs text-muted-foreground">vs {prevDate}</span>
											</div>
											<div className="mt-3 grid grid-cols-3 gap-3">
												{deltas.map((d) => {
													const diff = d.now - d.before;
													const tone = diff === 0
														? 'text-muted-foreground'
														: (diff > 0) === d.badIsUp
															? 'text-red-600'
															: 'text-green-600';
													return (
														<div key={d.label}>
															<div className="text-xs text-muted-foreground">{d.label}</div>
															<div className="mt-0.5 flex items-baseline gap-1.5">
																<span className="text-lg font-semibold text-foreground">{d.now}</span>
																<span className={`text-xs font-medium ${tone}`}>
																	{diff === 0 ? '±0' : diff > 0 ? `+${diff}` : `${diff}`}
																</span>
															</div>
														</div>
													);
												})}
											</div>
										</div>
									);
								})()}

								{/* Results Stats */}
								{(isCompleted || flaggedPosts.length > 0) && (
									<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
										<StatCard
											label="Total Posts"
											value={scan.results?.totalPosts || scan.progress?.postsFound || 0}
										/>
										<StatCard
											label="Posts Scanned"
											value={scan.results?.analyzedPosts || scan.progress?.postsAnalyzed || scan.results?.totalPosts || scan.progress?.postsFound || 0}
										/>
										<StatCard
											label="Flagged Posts"
											value={scan.results?.flaggedPosts || flaggedPosts.length}
										/>
									</div>
								)}

							</div>
						)}

						{/* Flagged Content Tab */}
						{activeTab === 'flagged' && (
							<Card>
								<CardContent className="p-6">
									<div className="flex items-center justify-between mb-4">
										<h3 className="text-lg font-semibold">Flagged Content</h3>
										<Button onClick={refresh} variant="outline" size="sm">
											Refresh
										</Button>
									</div>

									{flaggedPosts.length > 0 ? (
										<div className="space-y-4">
											{flaggedPosts.map((post, index) => (
												<Card key={post.id || index} className="border-l-4 border-red-500">
													<CardContent className="p-4">
														<div className="flex items-start justify-between">
															<div className="flex-1">
																<div className="flex items-center gap-2 mb-3">
																	<Badge variant="destructive">
																		{post.analysis?.category || 'Flagged'}
																	</Badge>
																	<Badge variant="outline">
																		Risk: {post.analysis?.riskScore || 0}%
																	</Badge>
																	{post.analysis?.severity && (
																		<Badge variant="outline" className="capitalize">
																			Severity: {post.analysis.severity}
																		</Badge>
																	)}
																</div>

																{/* Post Text - the text itself is the link, so following
																    up on a flagged post is one click from reading it. */}
																<p className="text-foreground mb-3">
																	<PostLink url={post.url}>{post.text}</PostLink>
																</p>

																{/* Visual evidence */}
																{post.mediaUrls?.length > 0 && (
																	<a href={post.url || post.mediaUrls[0]} target="_blank" rel="noreferrer" className="block mb-3">
																		<img
																			src={post.mediaUrls[0]}
																			alt="Flagged post media"
																			loading="lazy"
																			className="max-h-48 rounded-lg border border-border object-cover"
																			onError={(e) => { e.currentTarget.style.display = 'none'; }}
																		/>
																	</a>
																)}

																{/* Detailed Reasoning - NEW */}
																{post.analysis?.reason && (
																	<div className="mb-3 p-3 bg-muted rounded-md border border-border">
																		<p className="text-sm font-semibold text-foreground mb-1">
																			Why This Was Flagged:
																		</p>
																		<p className="text-sm text-muted-foreground leading-relaxed">
																			{post.analysis.reason}
																		</p>
																	</div>
																)}

																<div className="text-sm text-muted-foreground">
																	@{post.author} • {formatDate(post.timestamp)}
																</div>
															</div>
															{/* A real anchor, not a button calling window.open: that
															    could not be middle-clicked, opened in a new tab, or
															    copied - everything a manager chasing a post wants. */}
															{post.url && (
																<a
																	href={post.url}
																	target="_blank"
																	rel="noopener noreferrer"
																	className="shrink-0 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground hover:border-input hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
																>
																	View post
																</a>
															)}
														</div>
													</CardContent>
												</Card>
											))}
										</div>
									) : (
										<EmptyState
											compact
											icon={CheckCircledIcon}
											title="No flagged content"
											description={isScanning
												? 'Content is still being analyzed…'
												: 'No concerning content was detected in this scan.'}
										/>
									)}
								</CardContent>
							</Card>
						)}

						{/* Analysis Tab */}
						{activeTab === 'analysis' && (
							<Card>
								<CardContent className="p-6">
									<h3 className="text-lg font-semibold mb-4">Detailed Analysis</h3>

									{isCompleted && Object.values(scan.results?.riskBreakdown || {}).some((n) => n > 0) ? (
										<div className="space-y-4">
											<h4 className="font-medium">Risk categories found</h4>
											<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
												{Object.entries(scan.results.riskBreakdown)
													.filter(([, count]) => count > 0)
													.sort((a, b) => b[1] - a[1])
													.map(([category, count]) => (
														<div key={category} className="bg-muted p-3 rounded">
															<div className="text-sm text-muted-foreground capitalize">
																{category.replace(/_/g, ' ')}
															</div>
															<div className="text-lg font-semibold">{count}</div>
														</div>
													))}
											</div>
										</div>
									) : (
										<EmptyState
											compact
											icon={BarChartIcon}
											title={isScanning ? 'Analysis in progress' : 'No analysis data'}
											description={isScanning
												? 'Detailed analysis is being performed…'
												: 'Analysis data will be available once the scan completes.'}
										/>
									)}
								</CardContent>
							</Card>
						)}
					</div>

					{/* Actions */}
					<div className="mt-8 flex justify-between">
						<Button onClick={() => navigate(PATHS.SCAN_LIST)} variant="outline">
							Back to Scans
						</Button>
						{isCompleted && (
							<div className="space-x-2">
								<Button variant="outline" onClick={() => window.print()}>
									Print Report
								</Button>
							</div>
						)}
					</div>
				</div>
			</div >
		</>
	);
};

export default ScanDetailPage; 